/**
 * Module-level cache for application detail snapshots fetched from Salesforce.
 *
 * Storing this outside React state ensures the cache survives navigation
 * without requiring a context provider, and a single Map is shared across all
 * component instances for the lifetime of the page session.
 *
 * Two paths populate this cache:
 *   1. PortalContext — pre-warms from the applications list (DB data, no synced_at)
 *      immediately after the list loads, then overwrites with fresh SF data once
 *      the background bulk-detail fetch completes.
 *   2. ApplicationView — populates after a successful individual SF detail fetch.
 *
 * TTL is 30 seconds. After expiry, ApplicationView falls back to its own
 * individual Salesforce sync call.
 */

export interface ApplicationDetailSnapshot {
    applicationStatus: string;
    stage: string;
    offerLetterUrl: string | null;
    showOfferLetterOnPortal: boolean;
    acceptanceLetterUrl: string | null;
    showAcceptanceLetterOnPortal: boolean;
    syncedAt: string | null;
}

interface CacheEntry extends ApplicationDetailSnapshot {
    fetchedAt: number;
}

const CACHE_TTL_MS = 30_000;

// Singleton — module-level so it survives React re-renders and navigation.
const detailCache = new Map<string, CacheEntry>();

// Expose the cache on window in development so it can be inspected from the
// browser console: window.__appDetailCache
if (import.meta.env.DEV) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__appDetailCache = detailCache;
}

/**
 * Returns the cached snapshot for the given application number, or null if
 * the entry is missing or older than CACHE_TTL_MS.
 */
export function getCachedApplicationDetail(
    applicationNumber: string,
): ApplicationDetailSnapshot | null {
    const entry = detailCache.get(applicationNumber);
    if (!entry) return null;
    if (Date.now() - entry.fetchedAt > CACHE_TTL_MS) {
        detailCache.delete(applicationNumber);
        return null;
    }
    const { fetchedAt: _fetchedAt, ...snapshot } = entry;
    return snapshot;
}

/**
 * Stores a single application detail snapshot. Called by ApplicationView after
 * a successful individual SF sync.
 */
export function setCachedApplicationDetail(
    applicationNumber: string,
    snapshot: ApplicationDetailSnapshot,
): void {
    detailCache.set(applicationNumber, { ...snapshot, fetchedAt: Date.now() });
}

/**
 * Stores multiple snapshots at once, all stamped with the same timestamp.
 * Called by PortalContext after the background bulk-detail fetch completes
 * (and also for the initial pre-warm from list data).
 */
export function bulkSetCachedApplicationDetails(
    entries: Array<{ applicationNumber: string; snapshot: ApplicationDetailSnapshot }>,
): void {
    const now = Date.now();
    for (const { applicationNumber, snapshot } of entries) {
        detailCache.set(applicationNumber, { ...snapshot, fetchedAt: now });
    }
}

/**
 * Forcefully removes a cached entry so the next ApplicationView visit
 * triggers a fresh Salesforce sync. Call this after a payment or any
 * action that changes application state server-side.
 */
export function invalidateCachedApplicationDetail(applicationNumber: string): void {
    detailCache.delete(applicationNumber);
}
